import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from '@mui/material';
import { Close, ChevronLeft, ChevronRight, Star as StarIcon, ArrowForward } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthProvider.tsx';
import UpgradeModal from '../../components/UpgradeModal.tsx';
import { PolaroidDeliveryDialog, CapsuleUnlockDialog } from './shop-dialogs.tsx'

interface Theme {
  name: string;
  price: number | string;
  image: string;
  description: string;
  preview: string;
  disabled?: boolean;
}


const themes: Theme[] = [
  {
    name: 'Christmas',
    price: 'Coming Soon',
    image: '/christmas.jpg?height=300&width=400',
    description: 'Cozy winter cabin theme with snow and warm lighting',
    preview: '/christmas-preview.png',
    disabled: true
  },
  {
    name: 'New Year',
    price: 'Coming Soon',
    image: '/nye.jpg?height=300&width=400',
    description: 'Celebrate new beginnings with sparkles and fireworks',
    preview: '/newyear-preview.png',
    disabled: true
  },
  {
    name: 'Valentine\'s Day',
    price: 'Coming Soon',
    image: '/valentines.jpg?height=300&width=400',
    description: 'Romantic theme for your special moments',
    preview: '/valentines-preview.png',
    disabled: true
  },
  {
    name: 'Wedding Day',
    price: 'Coming Soon',
    image: '/wedding.jpg?height=300&width=400',
    description: 'Romantic theme for your special moments',
    preview: '/valentines-preview.png',
    disabled: true
  }
];

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    background: #FFFFFF;
    width: 100%;
    max-width: 800px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  position: relative;
  padding: 24px;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 8px;
  color: grey;
`;

const StyledButton = styled(Button)`
  border-radius: 8px;
  text-transform: none;
  background-color: #7B2CBF;
  &:hover {
    background-color: #6A24A6;
  }
`;


function ThemeDetailsDialog({ theme, open, onClose }: { theme: Theme; open: boolean; onClose: () => void }) {
  const [activeStep, setActiveStep] = useState(0);
  const useThemes = useTheme();
  const isMobile = useMediaQuery(useThemes.breakpoints.down('sm'));
  let steps:any[] = [];
  if(!isMobile){
    steps = ['Preview Theme', 'Confirm Purchase', 'Complete'];
  }
  else{steps = ['','','']}


  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = () => {
    // Implement purchase completion logic here
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogContent>
        <CloseButton aria-label="close" onClick={onClose}>
          <Close />
        </CloseButton>
        <Typography variant="h4" fontWeight="bold" color="#7B2CBF" gutterBottom>
          {theme.name} Theme
        </Typography>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, mt: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeStep === 0 && (
              <Box>
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  This theme is a lifetime purchase. You can use it for all your future capsules!
                </Alert>
                <Box sx={{display: 'flex', flexDirection: 'row'}}>
                <img src={theme.image} alt={`${theme.name} theme preview`} style={{ width: '40%', borderRadius: '8px'}} />
                <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mx: 4, mt: 12}}>
                <Typography variant="body1" gutterBottom>{theme.description}</Typography>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Price: ${theme.price}</Typography>
                </Box>
                </Box>     
              </Box>
            )}
            {activeStep === 1 && (
              <Box>
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  Remember, this is a one-time purchase. You'll have lifetime access to this theme for all your capsules!
                </Alert>
                <Typography variant="h6" gutterBottom>Confirm Your Purchase</Typography>
                <Typography>Theme: {theme.name}</Typography>
                <Typography>Price: ${theme.price}</Typography>
                <Typography sx={{ mt: 2 }}>Please review your purchase before proceeding.</Typography>      
              </Box>
            )}
            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>Purchase Complete!</Typography>
                <Typography>Thank you for purchasing the {theme.name} theme.</Typography>
                <Typography>You can now use this theme in all your time capsules, present and future!</Typography>
              </Box>
            )}
          </motion.div>
        </AnimatePresence>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <StyledButton onClick={handleNext} variant="contained">
              Next
            </StyledButton>
          ) : (
            <StyledButton onClick={handleComplete} variant="contained">
              Finish
            </StyledButton>
          )}
        </Box>
      </StyledDialogContent>
    </StyledDialog>
  );
}


export default function ShopPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeThemeIndex, setActiveThemeIndex] = useState(0);
  const { user } = useAuth();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [polaroidDialogOpen, setPolaroidDialogOpen] = useState(false);
  const [capsuleUnlockDialogOpen, setCapsuleUnlockDialogOpen] = useState(false);
  const [capsuleUnlockType, setCapsuleUnlockType] = useState<'single' | 'all'>('single');

  const handleOpenUpgradeModal = () => setUpgradeModalOpen(true);
  const handleCloseUpgradeModal = () => setUpgradeModalOpen(false);

  const themeContainerRef = useRef<HTMLDivElement | null>(null);

  const handlePrevTheme = () => {
    if (activeThemeIndex > 0) {
      setActiveThemeIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleNextTheme = () => {
    if (activeThemeIndex < themes.length - 1) {
      setActiveThemeIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: handleNextTheme,
    onSwipedRight: handlePrevTheme,
    trackMouse: true
  });

  useEffect(() => {
    const container = themeContainerRef.current;
    if (container) {
      const scrollTo = (activeThemeIndex * container.offsetWidth) / (isMobile ? 1.5 : 3);
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  }, [activeThemeIndex, isMobile]);

  const handleThemeClick = (theme: Theme) => {
    if (!isDragging && !theme.disabled) {
      setSelectedTheme(theme);
    }
  };

  const handleCloseThemeDetails = () => setSelectedTheme(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setStartX(e.pageX - (themeContainerRef.current?.offsetLeft || 0));
    setScrollLeft(themeContainerRef.current?.scrollLeft || 0);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (themeContainerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 2;
    if (themeContainerRef.current) {
      themeContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const handleOpenPolaroidDialog = () => setPolaroidDialogOpen(true);
  const handleClosePolaroidDialog = () => setPolaroidDialogOpen(false);

  const handleOpenCapsuleUnlockDialog = (type: 'single' | 'all') => {
    setCapsuleUnlockType(type);
    setCapsuleUnlockDialogOpen(true);
  };
  const handleCloseCapsuleUnlockDialog = () => {
    setCapsuleUnlockDialogOpen(false);
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ minHeight: '100vh', bgcolor: '#0A0F15', color: 'white', p: 2 }}>
          <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ px: isMobile ? 0 : 2}}>
            {isMobile &&
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
              <Typography variant="h5" component="h1">
                Shop
              </Typography>
            </Box>
            }
            {user?.subscriptionStatus !== 'premium' && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <UpgradeContainer onClick={handleOpenUpgradeModal}>
                  <UpgradeContent>
                    <UpgradeIcon>
                      <StarIcon sx={{ fontSize: 24, color: '#7B2CBF' }} />
                    </UpgradeIcon>
                    <UpgradeInfo>
                      <UpgradeTitle>Upgrade to Premium</UpgradeTitle>
                      <UpgradeDescription>Unlock unlimited capsules and more!</UpgradeDescription>
                    </UpgradeInfo>
                  </UpgradeContent>
                  <UpgradeButton>
                    <ArrowForward />
                  </UpgradeButton>
                </UpgradeContainer>
              </motion.div>
            )}

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <Box sx={{ mb: 4 }} onClick={handleOpenPolaroidDialog}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                  Polaroid Delivery
                </Typography>
                <motion.div whileHover={{ scale: 1.02, cursor: 'pointer' }}>
                  <Card sx={{ bgcolor: '#1d2228', color: 'white', borderRadius: 4 }}>
                    <CardMedia
                      component="img"
                      height={isMobile ? "200" : "250"}
                      image="/print.jpg"
                      alt="Polaroid camera with delivery box"
                    />
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Polaroids of your best memories
                      </Typography>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" gutterBottom>
                        Delivered to your door when your capsule unlocks
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box sx={{ mb: 4, position: 'relative' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Seasonal Themes
                </Typography>
                <Box
                  {...handlers}
                  ref={themeContainerRef}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'scroll',
                    pb: 2,
                    width: isMobile ? '108%' : '100%',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    },
                    scrollSnapType: 'x mandatory',
                    cursor: isDragging ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseLeave={handleMouseLeave}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                >
                  <AnimatePresence initial={false}>
                    {themes.map((theme, index) => (
                      <motion.div
                        key={`${theme.name}-${index}`}
                        whileHover={{ scale: 1.02 }}
                        style={{
                          flex: '0 0 auto',
                          width: isMobile ? '60%' : '30%',
                          scrollSnapAlign: 'start',
                        }}
                        onClick={() => handleThemeClick(theme)}
                      >
                        <Card
                          sx={{
                            bgcolor: 'transparent',
                            background: 'none',
                            boxShadow: 'none',
                            color: 'white',
                            cursor: theme.disabled ? 'auto' : 'pointer',
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={theme.image}
                            alt={theme.name}
                            sx={{
                              height: isMobile ? 150 : 200,
                              borderRadius: 4,
                              mb: 1,
                            }}
                          />
                          <Box sx={{ px: 0 }}>
                            <Typography variant="h6" sx={{ mb: 0 }}>
                              {theme.name}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontSize: '1.25rem',
                                mt: 0
                              }}
                            >
                              {typeof theme.price === 'number' ? `$${theme.price}` : theme.price}
                            </Typography>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </Box>
                {!isMobile && (
                  <>
                    <IconButton
                      onClick={handlePrevTheme}
                      disabled={activeThemeIndex === 0}
                      sx={{
                        color: 'white',
                        position: 'absolute',
                        left: -50,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                        '&.Mui-disabled': { opacity: 0.5, pointerEvents: 'none' },
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <IconButton
                      onClick={handleNextTheme}
                      disabled={activeThemeIndex === themes.length - (themes.length - 1)}
                      sx={{
                        color: 'white',
                        position: 'absolute',
                        right: -50,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                        '&.Mui-disabled': { opacity: 0.5, pointerEvents: 'none' },
                      }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </>
                )}
              </Box>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Instant Capsule Unlock
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <motion.div  onClick={() => handleOpenCapsuleUnlockDialog('single')} whileHover={{ scale: 1.02, cursor: 'pointer' }}>
                      <Card sx={{ bgcolor: '#1d2228', color: 'white', borderRadius: 4 }}>
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="h6">Unlock 1 capsule</Typography>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                              Get your photos right away
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="h6" color="#FFF">$5</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <motion.div  onClick={() => handleOpenCapsuleUnlockDialog('all')} whileHover={{ scale: 1.02, cursor: 'pointer' }}>
                      <Card sx={{ bgcolor: '#1d2228', color: 'white', borderRadius: 4 }}>
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="h6">Unlock all capsules</Typography>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                              Get your photos right away
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography variant="h6" color="#FFF">$12</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                </Grid>
              </Box>
            </motion.div>
          </Container>
        </Box>
      </motion.div>
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={handleCloseUpgradeModal}
      />
      {selectedTheme && (
        <ThemeDetailsDialog
          theme={selectedTheme}
          open={Boolean(selectedTheme)}
          onClose={handleCloseThemeDetails}
        />
      )}
      <PolaroidDeliveryDialog
        open={polaroidDialogOpen}
        onClose={handleClosePolaroidDialog}
        User={user!.username}
      />
      <CapsuleUnlockDialog
        open={capsuleUnlockDialogOpen}
        onClose={handleCloseCapsuleUnlockDialog}
        unlockType={capsuleUnlockType}
        User={user!.username}
      />
    </Box>
  );
}

const UpgradeContainer = styled.div`
  display: flex;
  margin-left: 0%;
  margin-right: 0%;
  margin-bottom: 3%;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  position: relative;
  height: 80px;
  border: 4px solid transparent;
  background-clip: padding-box;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff);
    z-index: -1;
    border-radius: 16px;
  }

  &:hover {
    box-shadow: 0 10px 20px rgba(123, 44, 191, 0.2);
  }

  @media (min-width: 768px) {
    padding: 20px 32px;
  }
`;

const UpgradeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UpgradeIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(123, 44, 191, 0.1);
  border-radius: 50%;
`;

const UpgradeInfo = styled.div`
  flex: 1;
  margin-top: 15px;
`;

const UpgradeTitle = styled.h3`
  color: #7B2CBF;
  font: 700 16px/1.2 Inter, sans-serif;
  margin-bottom: 4px;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const UpgradeDescription = styled.p`
  color: #666;
  font: 400 12px Inter, sans-serif;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const UpgradeButton = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(123, 44, 191, 0.1);
  border-radius: 50%;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(123, 44, 191, 0.2);
  }

  svg {
    color: #7B2CBF;
    font-size: 20px;
  }
`;